// src/ClinicalDataCollector/components/output/FollowUpExamSummary.jsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const FollowUpExamSummary = () => {
  // 🔹 Id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD (Follow Up Exam 2.0)
  const {
    followUpExamNotes,
    followUpExamLoading,
    followUpExamError,
    loadFollowUpExamNotes,
  } = useContext(AdvancedMdContext);

  // 🔹 Controlar qué notas están expandidas
  const [openMap, setOpenMap] = useState({});

  const toggleNote = noteId => {
    setOpenMap(prev => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  // 🔹 Cargar notas cuando tengamos advancedMdPatientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadFollowUpExamNotes(advancedMdPatientId);
  }, [advancedMdPatientId, loadFollowUpExamNotes]);

  const notes = followUpExamNotes || [];
  const totalNotes = notes.length;

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      {/* Título */}
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <EventNoteIcon color="primary" />
        <Box ml={1}>Clinical Notes – Follow Up Exam 2.0</Box>
      </Typography>

      {/* Resumen de cuántas notas hay */}
      {!followUpExamLoading && !followUpExamError && totalNotes > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {totalNotes === 1
            ? '1 clinical note'
            : `${totalNotes} clinical notes`}
        </Typography>
      )}

      {followUpExamLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading Follow Up Exam notes from AdvancedMD…
        </Typography>
      )}

      {followUpExamError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading Follow Up Exam notes:{' '}
          {followUpExamError.message || 'Unknown error'}
        </Alert>
      )}

      {!followUpExamLoading &&
        notes.length === 0 &&
        !followUpExamError && (
          <Typography variant="body2" color="text.secondary">
            No Follow Up Exam 2.0 notes available for this patient.
          </Typography>
        )}

      {/* 🔹 Notas individuales */}
      {notes.map(note => {
        const f = note.fields || {};
        const dateLabel = note.noteDateTime
          ? new Date(note.noteDateTime).toLocaleString()
          : 'N/A';

        const bpCurrent =
          f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
            ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
            : '';

        const bpPrev =
          f.Vitals_BP_SystolicPrev && f.Vitals_BP_DiastolicPrev
            ? `${f.Vitals_BP_SystolicPrev}/${f.Vitals_BP_DiastolicPrev}`
            : '';

        // 🔹 Mini resumen de signos vitales para el encabezado
        const vitalsSummary = [
          f.Vitals_Weight ? `Wt: ${f.Vitals_Weight}` : null,
          f.Vitals_BMI ? `BMI: ${f.Vitals_BMI}` : null,
          bpCurrent ? `BP: ${bpCurrent}` : null,
        ]
          .filter(Boolean)
          .join(' • ');

        const isOpen = !!openMap[note.noteId];

        return (
          <Box
            key={note.noteId}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header de la nota (compacto) */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {note.templateName} – {dateLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Entered by: {note.userName} | Signed by: {note.signedByUser}
              {note.reviewedByUser
                ? ` | Reviewed by: ${note.reviewedByUser}`
                : ''}
            </Typography>

            {vitalsSummary && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {vitalsSummary}
              </Typography>
            )}

            {/* Botón para ver/ocultar detalle */}
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => toggleNote(note.noteId)}
              >
                {isOpen ? 'Hide details' : 'View details'}
              </Button>
            </Box>

            {/* Detalle completo dentro de Collapse */}
            <Collapse in={isOpen}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Vitals */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vitals (from note)
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Weight</TableCell>
                        <TableCell>{f.Vitals_Weight || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Height</TableCell>
                        <TableCell>{f.Vitals_Height || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BMI</TableCell>
                        <TableCell>{f.Vitals_BMI || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Temp</TableCell>
                        <TableCell>{f.Vitals_Temperature || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Pulse</TableCell>
                        <TableCell>{f.Vitals_Pulse || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>O₂ Sat RA</TableCell>
                        <TableCell>{f.Vitals_O2SatRA || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BP (current)</TableCell>
                        <TableCell>{bpCurrent || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BP (previous)</TableCell>
                        <TableCell>{bpPrev || '—'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>

                {/* Chief complaint + meds actuales en la nota */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Chief Complaints
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {f.ChiefComplaints || '—'}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Current Medications (note)
                    </Typography>
                    <Typography variant="body2">
                      {f.CurrentMedications || '—'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Assessment + plan (treatment) */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assessments
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {f.Assessments || '—'}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Treatment Plan
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {f.Treatment || f.Treatment1 || '—'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default FollowUpExamSummary;
